import { EllipsisOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import styled from 'styled-components';

import BookmarkIcon from '@/assets/icons/BookmarkIcon';
import CountBadge from '@/components/Badge/CountBadge/CountBadge';
import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Menu from '@/components/Menu/Menu';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { useToast } from '@/stores/pure/ToastStore';
import { TAXONOMY_CATEGORY } from '@/types/taxonomyConst';
import { clipboardCopy } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import {
  buildTaxonomyFeedbackPath,
  buildWorkspaceTaxonomyPath,
} from '../../utils/routes';
import predictionStateUtils from '../Topic2Detail/PredictionStateDisplay/utils';

type Props = TaxonomyT & {
  callbacks: {
    onClick: (alias: string) => void;
    onStarClick: (alias: string) => void;
    onDeleteClick: (alias: string) => void;
    onEditClick: (alias: string) => void;
  };
};

const TaxonomyCategoryIcon = (props: { category: TAXONOMY_CATEGORY }) => {
  if (props.category === TAXONOMY_CATEGORY.SAVED_TOPIC)
    return (
      <IconWrapper>
        <BookmarkIcon />
      </IconWrapper>
    );
  return null;
};

const IconWrapper = styled.span`
  padding-right: 6px;
  font-size: 18px;
  display: inline-block;
  transform: translateY(2px);
`;

const TaxonomyListItem = (props: Props) => {
  const addToast = useToast();
  const isPrediction = predictionStateUtils.isPrediction(
    props.prediction_state,
  );

  const options = useMemo(() => {
    const baseOptions = [
      {
        label: 'Share',
        onClick: () => {
          const wa = getCurrWorkspaceAlias();
          clipboardCopy(
            `${window.location.origin}${buildWorkspaceTaxonomyPath(
              wa,
              buildTaxonomyFeedbackPath(props.alias),
            )}`,
          );
          addToast('Taxonomy URL copied', 'success');
        },
      },
    ];

    if (props.category === TAXONOMY_CATEGORY.DEFAULT) {
      baseOptions.push(
        {
          label: 'Edit',
          onClick: () => props.callbacks.onEditClick(props.alias),
        },
        {
          label: 'Delete',
          onClick: () => props.callbacks.onDeleteClick(props.alias),
        },
      );
    }

    return baseOptions;
  }, [addToast, props.alias, props.callbacks, props.category]);

  return (
    <Style
      j="space-between"
      onClick={() => props.callbacks.onClick(props.alias)}
    >
      <Flex j="space-between" a="flex-start">
        <Heading
          type="h3"
          style={{ height: 48 }}
          multilineEllipsis={2}
          weight="sb"
        >
          <TaxonomyCategoryIcon category={props.category} />
          {props.title}
        </Heading>
        <Flex g={4}>
          {isPrediction ? (
            <Spinner />
          ) : (
            <>
              <div className="hover-item">
                <Menu
                  triggerNode={
                    <IconButton
                      icon={<EllipsisOutlined />}
                      sizeType="m"
                      outlined={false}
                    />
                  }
                  options={options}
                />
              </div>
              <div className={!props.starred ? 'hover-item' : undefined}>
                <StarredButton
                  starred={props.starred}
                  onClick={() => props.callbacks.onStarClick(props.alias)}
                />
              </div>
            </>
          )}
        </Flex>
      </Flex>
      <VFlex g={16}>
        <Typo type="body2" multilineEllipsis={2}>
          {props.desc}
        </Typo>
        <Flex a="center" g={4}>
          <CountBadge count={props.topic_count} />
          <Typo type="label1" weight="m">
            Topics
          </Typo>
        </Flex>
      </VFlex>
    </Style>
  );
};

const Style = styled(VFlex)`
  cursor: pointer;
  display: flex;
  flex: 1;
  min-width: 320px;
  width: 100%;
  height: 220px;
  padding: 28px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${(props) => props.theme.radius.card}px;
  box-shadow: ${(props) => props.theme.shadow[2]};
  background-color: ${(props) => props.theme.colors.gray100};
  transition: all 0.2s;
  &:hover {
    transform: translateY(-6px);
  }
  .hover-item {
    opacity: 0;
    transition: all 0.2s;
  }
  &:hover {
    .hover-item {
      opacity: 1;
    }
  }
`;

type StarredProps = {
  starred: boolean;
  onClick: () => void;
};

const StarredButton = (props: StarredProps) => {
  const color = useColor();
  const StarIcon = useMemo(() => {
    if (props.starred) return <StarFilled style={{ color: color.yellow500 }} />;
    return <StarOutlined />;
  }, [color.yellow500, props.starred]);

  return (
    <IconButton
      icon={StarIcon}
      outlined={false}
      sizeType="m"
      onClick={props.onClick}
      style={
        props.starred
          ? {
              color: color.yellow500,
              background: color.gray200,
              border: `1px solid ${color.gray300}`,
            }
          : {
              color: color.gray600,
            }
      }
    />
  );
};

export default TaxonomyListItem;
